import React from 'react';
import Typography from '@material-ui/core/Typography';
import { BarChart, Bar, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useColorMode } from '../../../../theme/ColorModeProvider';
import { getChartPalette } from '../../../../theme/colorMode';
import { Section, Panel, MetricGrid, DataTable, EmptyState } from './shared';
import { formatCount, formatPercent } from '../utils';
import { useInsightsStyles } from '../styles';
import Button from '@material-ui/core/Button';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Link } from 'react-router-dom';
import { Paths } from '../../../../Routes/routePaths';

export const ProjectsSection = ({ projects = {} }) => {
  const classes = useInsightsStyles();
  const statuses = (projects.by_status || []).map(item => ({ ...item, warning: item.key === 'overdue' && Number(item.value) > 0 }));
  return (
    <Section id="projects" title="Projects" subtitle={projects.total !== undefined ? `${formatCount(projects.total)} projects` : undefined} exportKey="projects">
      <Panel>
        {statuses.length ? <MetricGrid items={statuses} columns={Math.min(5, statuses.length)} /> : <EmptyState />}
        <Typography className={classes.footnote}>
          {formatCount(projects.recruitment_projects)} recruitment projects · {formatCount(projects.deleted_projects_excluded)} deleted projects not counted
        </Typography>
        <Button component={Link} to={Paths.SuperadminProjects} color="primary" endIcon={<ArrowForwardIcon />} style={{ marginTop: 12 }}>
          View all projects
        </Button>
      </Panel>
    </Section>
  );
};

const topColumns = [
  { key: 'name', label: 'Course', render: row => <span style={{ fontWeight: 600 }}>{row.name}</span> },
  { key: 'provider', label: 'Provider', render: row => row.provider || '-' },
  { key: 'category', label: 'Category', render: row => row.category || '-' },
  { key: 'assigned', label: 'Assigned', align: 'right', render: row => formatCount(row.assigned) },
  { key: 'started', label: 'Started', align: 'right', render: row => formatCount(row.started) },
  { key: 'completed', label: 'Completed', align: 'right', render: row => formatCount(row.completed) },
  { key: 'start_rate_pct', label: 'Start rate', align: 'right', render: row => formatPercent(row.start_rate_pct) },
  { key: 'completion_rate_pct', label: 'Completion rate', align: 'right', render: row => formatPercent(row.completion_rate_pct) },
];

const providerColumns = [
  { key: 'provider', label: 'Provider', render: row => <span style={{ fontWeight: 600 }}>{row.provider}</span> },
  { key: 'assigned', label: 'Seats assigned', align: 'right', render: row => formatCount(row.assigned) },
  { key: 'started', label: 'Started', align: 'right', render: row => formatCount(row.started) },
  { key: 'completed', label: 'Completed', align: 'right', render: row => formatCount(row.completed) },
  { key: 'completion_rate_pct', label: 'Completion rate', align: 'right', render: row => formatPercent(row.completion_rate_pct) },
  { key: 'learning_hub_enrollments', label: 'Learning Hub enrolments', align: 'right', render: row => formatCount(row.learning_hub_enrollments) },
  { key: 'learning_hub_completed', label: 'Learning Hub completed', align: 'right', render: row => formatCount(row.learning_hub_completed) },
];

export const CoursesSection = ({ courses = {} }) => {
  const classes = useInsightsStyles();
  const chart = getChartPalette(useColorMode().mode);
  const catalog = courses.catalog_by_provider || [];
  const catalogMax = Math.max(1, ...catalog.map(item => Number(item.courses) || 0));
  const providers = courses.by_provider || [];
  return (
    <Section id="courses" title="Courses" subtitle={courses.note} exportKey="courses">
      <Panel>
        {catalog.length ? <>
          <Typography component="h3" className={classes.sectionTitle} style={{ marginBottom: 8 }}>
            Active catalogue · {formatCount(courses.catalog_active)} courses
          </Typography>
          <div className={classes.tile} style={{ marginBottom: 18 }}>
            <ul aria-label="Active catalogue courses by provider" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', columnGap: 28, rowGap: 12 }}>
              {catalog.map(item => (
                <li key={item.provider} style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <Typography style={{ fontSize: '.8rem', fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{item.provider}</Typography>
                    <Typography style={{ fontSize: '.8rem', whiteSpace: 'nowrap' }}><strong>{formatCount(item.courses)}</strong></Typography>
                  </div>
                  <div style={{ height: 6, borderRadius: 6, marginTop: 5, background: 'var(--dm-border, #e7ebf0)', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.max((Number(item.courses) || 0) / catalogMax * 100, item.courses ? 1.5 : 0)}%`, height: '100%', borderRadius: 6, background: '#3D7DDD' }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </> : null}
        <Typography component="h3" className={classes.sectionTitle} style={{ marginBottom: 8 }}>Most assigned courses</Typography>
        <DataTable ariaLabel="Most assigned courses" columns={topColumns} rows={courses.top || []} getRowKey={row => row.course_id} emptyText={courses.note} />
        <Typography component="h3" className={classes.subheading}>By provider</Typography>
        {providers.length ? <>
          <div className={classes.chart} style={{ height: Math.max(260, providers.length * 64 + 70) }} role="img" aria-label="Bar chart of course seats and Learning Hub enrolments by provider. The values are in the table below.">
            <ResponsiveContainer>
              <BarChart data={providers} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={chart.grid} horizontal={false} />
                <XAxis type="number" stroke={chart.axis} tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="provider" width={150} stroke={chart.axis} tick={{ fontSize: 12 }} interval={0} />
                <Tooltip contentStyle={{ background: chart.tooltipBg, borderColor: chart.tooltipBorder }} formatter={value => formatCount(value)} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="assigned" name="Assigned" fill="#3D7DDD" radius={[0, 4, 4, 0]} />
                <Bar dataKey="started" name="Started" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                <Bar dataKey="completed" name="Completed" fill="#16a34a" radius={[0, 4, 4, 0]} />
                <Bar dataKey="learning_hub_enrollments" name="Learning Hub enrolments" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 12 }}>
            <DataTable ariaLabel="Courses by provider" columns={providerColumns} rows={providers} getRowKey={row => row.provider} />
          </div>
        </> : <EmptyState>{courses.note}</EmptyState>}
      </Panel>
    </Section>
  );
};

const ProjectsAndCourses = ({ projects = {}, courses = {} }) => (
  <>
    <ProjectsSection projects={projects} />
    <CoursesSection courses={courses} />
  </>
);

export default ProjectsAndCourses;
